import type { ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import { PButton } from "./PButton";

// A PRIMENG `p-dialog` / HEADLESS `p-confirmDialog` FRAME IN THE PILLARS THEMES: 4PX RADIUS,
// A 1PX `rgba(0,0,0,.2)` BORDER AND NO SHADOW, ON `--surface-overlay`, OVER A `--maskbg`
// MASK. WITHOUT `header` IT IS HEADLESS - THE CONTENT DRAWS ITS OWN PADDING, AS THE
// HEADLESS TEMPLATES DO (`p-5` ETC.). WITH `header` IT IS A `p-dialog`: A 1REM HEADER
// (1.25REM / 600 TITLE, A 2REM ROUND CLOSE BUTTON, A 1PX RULE BELOW) OVER 1REM CONTENT.
// THE WIDTH IS THE CONTENT'S UNLESS `width` IS GIVEN.
export function PDialog(props: {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  header?: ReactNode;
  // A `p-dialog-footer`: 1PX RULE ABOVE, 1REM PADDING, RIGHT-ALIGNED
  footer?: ReactNode;
  width?: string | Record<string, string>;
  // FALSE KEEPS ESCAPE AND MASK CLICKS FROM CLOSING IT (`closable` / `dismissableMask`)
  dismissible?: boolean;
  // FALSE KEEPS ONLY MASK CLICKS FROM CLOSING IT, AS A `p-dialog` WITHOUT `dismissableMask` DOES
  // (ESCAPE STILL CLOSES). DEFAULTS TO `dismissible`.
  maskClosable?: boolean;
  children: ReactNode;
}) {
  const { open, onClose, ariaLabel, header, footer, width, dismissible = true, maskClosable = dismissible, children } = props;
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (!dismissible) return;
        if (reason === "backdropClick" && !maskClosable) return;
        onClose();
      }}
      maxWidth={false}
      slotProps={{
        paper: {
          "aria-label": ariaLabel,
          sx: {
            m: "14px",
            width,
            maxWidth: "calc(100% - 28px)",
            borderRadius: "4px",
            border: "1px solid rgba(0, 0, 0, 0.2)",
            boxShadow: "none",
            backgroundColor: "var(--surface-overlay)",
            color: "var(--text-color)",
          },
        },
        backdrop: { sx: { backgroundColor: "var(--maskbg)" } },
      }}
    >
      {header === undefined ? (
        children
      ) : (
        <>
          <div
            className="flex align-items-center justify-content-between"
            style={{ padding: "1rem", borderBottom: "1px solid var(--pillars-dialog-rule)", flexShrink: 0 }}
          >
            <span style={{ fontWeight: 600, fontSize: "1.25rem" }}>{header}</span>
            <PButton icon="times" rounded variant="text" severity="secondary" ariaLabel="Close" onClick={onClose} sx={{ width: "2rem", height: "2rem" }} />
          </div>
          <div style={{ padding: "1rem", overflowY: "auto" }}>{children}</div>
          {footer && (
            <div className="flex justify-content-end gap-2" style={{ padding: "1rem", borderTop: "1px solid var(--pillars-dialog-rule)" }}>
              {footer}
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}
